import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useGetCaseById } from '@hooks/react-query/queries/useGetCaseById';
import { useGetUserPermissions } from '@hooks/react-query/queries/useGetUserPermissions';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ClaimScoreLoader } from 'ui';
import { Page, useAppLocation } from '@hooks/customHooks/useAppLocation';
import { decodeSearchParams } from '@pages/CaseDashboard/utils';
import { AccountOrganization } from 'services/ClaimscoreApiService/Accounts/types';
import { Case } from 'services/ClaimscoreApiService/Cases/types';
import { CasesPermissions } from 'services/ClaimscoreApiService/Permissions/types';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';
import {
  RoleLevel,
  UserOrganizationPermissions,
  UserOrgCaseContextProps,
  UserOrgCaseFilters,
} from './UserOrgCase.types';

export const UserOrgCaseContext = createContext<UserOrgCaseContextProps>({
  userOrganizations: [] as AccountOrganization[],
  userCases: [] as CasesPermissions[],
  selectedOrganization: {} as AccountOrganization,
  selectedCase: {} as Case,
  isAdminOrOwnerInAnyOrgOrCase: false,
  handleOrganizationSelect: () => undefined,
  handleCaseSelect: () => undefined,
  isLoading: true,
  getUserRole: () => undefined,
  filters: {} as UserOrgCaseFilters,
});

export function UserOrgCaseProvider({ children }: PropsWithChildren) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, shouldShowOrgOrCase } = useAppLocation();

  const filters: UserOrgCaseFilters = decodeSearchParams(searchParams);

  const { data: userPermissions, isLoading: isLoadingOrg } =
    useGetUserPermissions();

  const userOrganizations: UserOrganizationPermissions[] = useMemo(() => {
    if (!userPermissions || userPermissions.length === 0) return [];

    let organizations = userPermissions.map(
      ({ organizationPermission, casesPermissions }) => ({
        ...organizationPermission,
        casesPermissions,
      }),
    );

    if (page === Page.permissions) {
      organizations = organizations.filter(
        (org) =>
          org.roleName === UserRole.Admin || org.roleName === UserRole.Owner,
      );
    }
    return organizations;
  }, [page, userPermissions]);

  const getAllowedCasesByOrg = useCallback(
    (orgPermissions: UserOrganizationPermissions) => {
      if (!userOrganizations || userOrganizations.length === 0) return [];

      let cases = orgPermissions
        ? orgPermissions.casesPermissions
        : userOrganizations[0].casesPermissions;

      if (page === Page.permissions) {
        cases = cases.filter(
          (currentCase) =>
            currentCase.roleName === UserRole.Admin ||
            currentCase.roleName === UserRole.Owner,
        );
      }

      return cases;
    },
    [page, userOrganizations],
  );

  const userCases = useMemo(() => {
    if (!userOrganizations || userOrganizations.length === 0) return [];

    const organization = userOrganizations.find(
      ({ organizationID }) => organizationID === filters.orgId,
    );

    const selectedOrg = organization || userOrganizations[0];
    return getAllowedCasesByOrg(selectedOrg);
  }, [userOrganizations, getAllowedCasesByOrg, filters.orgId]);

  const { data: selectedCase, isLoading: isLoadingCase } = useGetCaseById(
    filters.caseId || userCases[0]?.caseID || '',
  );

  useEffect(() => {
    if (!filters.orgId && shouldShowOrgOrCase) {
      setSearchParams(
        (prev) => {
          prev.set('orgId', userOrganizations[0]?.organizationID || '');
          prev.set('caseId', userCases[0]?.caseID || '');
          return prev;
        },
        { state: location.state },
      );
    }
  }, [
    filters.caseId,
    filters.orgId,
    location.state,
    setSearchParams,
    shouldShowOrgOrCase,
    userCases,
    userOrganizations,
  ]);

  const handleCaseSelect = useCallback(
    (newSelectedCase: CasesPermissions) => {
      setSearchParams(
        (prev) => {
          prev.set('caseId', newSelectedCase.caseID);
          return prev;
        },
        { state: location.state },
      );
    },
    [location.state, setSearchParams],
  );

  const handleOrganizationSelect = useCallback(
    (selectedOrganization: AccountOrganization) => {
      if (!userOrganizations || userOrganizations.length === 0) return;

      const selectedOrganizationId = selectedOrganization.organizationID;

      const organizationPermissions = userOrganizations.find(
        ({ organizationID }) => organizationID === selectedOrganizationId,
      );

      if (!organizationPermissions) return;

      const cases = getAllowedCasesByOrg(
        organizationPermissions || userOrganizations[0],
      );

      const currentCase = cases.find(({ caseID }) => caseID === filters.caseId);

      if (!currentCase && cases.length === 0) {
        setSearchParams(
          (prev) => {
            prev.set('orgId', organizationPermissions?.organizationID);
            return prev;
          },
          { state: location.state },
        );
        return;
      }

      const selectedCaseId = currentCase ? currentCase.caseID : cases[0].caseID;

      setSearchParams(
        (prev) => {
          prev.set('orgId', organizationPermissions?.organizationID);
          prev.set('caseId', selectedCaseId);
          return prev;
        },
        { state: location.state },
      );
    },
    [
      filters.caseId,
      location.state,
      userOrganizations,
      getAllowedCasesByOrg,
      setSearchParams,
    ],
  );

  const getUserRole = useCallback(
    (level: RoleLevel) => {
      const selectedOrgPermissions = userOrganizations.find(
        ({ organizationID }) => organizationID === filters.orgId,
      );

      if (level === RoleLevel.organization) {
        return selectedOrgPermissions?.roleName as UserRole | undefined;
      }

      if (level === RoleLevel.case) {
        const selectedCasePermissions =
          selectedOrgPermissions?.casesPermissions.find(
            ({ caseID }) => caseID === filters.caseId,
          );

        return selectedCasePermissions?.roleName as UserRole | undefined;
      }
    },
    [filters.caseId, filters.orgId, userOrganizations],
  );

  const isAdminOrOwnerInAnyOrgOrCase = useMemo(() => {
    const allowedRoles = [UserRole.Admin, UserRole.Owner];
    const isOwnerOrAdminInAnyOrg = userOrganizations.some((organization) =>
      allowedRoles.includes(organization.roleName as UserRole),
    );
    const isOwnerOrAdminInAnyCase = userCases.some((currentCase) =>
      allowedRoles.includes(currentCase.roleName as UserRole),
    );
    return isOwnerOrAdminInAnyOrg || isOwnerOrAdminInAnyCase;
  }, [userCases, userOrganizations]);

  const UserOrgCaseProviderValues = useMemo(
    () => ({
      userOrganizations,
      userCases,
      selectedOrganization:
        userOrganizations.find(
          ({ organizationID }) => organizationID === filters.orgId,
        ) || userOrganizations[0],
      selectedCase: selectedCase || (userCases[0] as unknown as Case),
      isAdminOrOwnerInAnyOrgOrCase,
      handleOrganizationSelect,
      handleCaseSelect,
      getUserRole,
      isLoading: isLoadingOrg || isLoadingCase,
      filters,
    }),
    [
      userOrganizations,
      userCases,
      selectedCase,
      isAdminOrOwnerInAnyOrgOrCase,
      handleOrganizationSelect,
      handleCaseSelect,
      getUserRole,
      isLoadingOrg,
      isLoadingCase,
      filters,
    ],
  );

  if (isLoadingOrg) {
    return <ClaimScoreLoader />;
  }

  return (
    <UserOrgCaseContext.Provider value={UserOrgCaseProviderValues}>
      {children}
    </UserOrgCaseContext.Provider>
  );
}

export const useUserOrgCase = () => useContext(UserOrgCaseContext);
